import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowRight, Trophy, Users, MapPin, Calendar, Radio } from "lucide-react";
import api from "@/lib/api";
import TournamentCard from "@/components/TournamentCard";
import { Button } from "@/components/ui/button";

export default function HomePage() {
  const [tournaments, setTournaments] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [rankGender, setRankGender] = useState("M");   // M = mens_singles 19+, F = womens_singles 19+
  const [stars, setStars] = useState([]);
  const [news, setNews] = useState([]);
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    api.get("/tournaments").then((r) => setTournaments(r.data));
    api.get("/news?limit=5").then((r) => setNews(r.data));
    api.get("/sponsors").then((r) => setSponsors(r.data));
    api.get("/jpa-stars").then((r) => setStars(r.data)).catch(() => setStars([]));
  }, []);

  useEffect(() => {
    const eventCategory = rankGender === "F" ? "womens_singles" : "mens_singles";
    api.get(`/rankings?event_category=${eventCategory}&age=19%2B&limit=10`).then((r) => setRankings(r.data)).catch(() => setRankings([]));
  }, [rankGender]);

  const running = tournaments.filter((t) => t.status === "running");
  // Latest 4 upcoming tournaments regardless of division (state/national/international/city/district)
  const upcoming = tournaments
    .filter((t) => t.status === "upcoming")
    .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""))
    .slice(0, 4);
  const featuredNews = news[0];
  const otherNews = news.slice(1, 5);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[86vh] bg-jpa-ink overflow-hidden">
        {/* Background video with image poster */}
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-60"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="https://images.unsplash.com/photo-1737476997205-b3336182f215?w=1800"
          data-testid="hero-video"
        >
          <source src="/media/hero.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-r from-jpa-ink via-jpa-ink/85 to-transparent" />
        <div className="absolute inset-0 grain" />

        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 pt-24 pb-32 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 text-white">
            <span className="inline-flex items-center gap-2 bg-jpa-accent text-jpa-ink px-3 py-1 text-xs font-bold uppercase tracking-widest skew-badge">
              <Radio className="w-3.5 h-3.5" /> Official state body • AIPA affiliated
            </span>
            <h1 className="mt-6 font-display leading-[0.85] tracking-wider">
              <span className="block text-6xl md:text-8xl lg:text-9xl">JHARKHAND</span>
              <span className="block text-jpa-accent text-6xl md:text-8xl lg:text-9xl mt-1">PICKLEBALL</span>
              <span className="block text-white/80 text-xl md:text-2xl lg:text-3xl mt-3 tracking-[0.3em]">ASSOCIATION</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg md:text-xl text-white/85 leading-relaxed">
              The state&apos;s official governing body — sanctioning tournaments, ranking players and building the next generation of Indian pickleball champions.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/tournaments" data-testid="hero-register-btn">
                <Button size="lg" className="bg-jpa-accent hover:bg-[#BEE617] text-jpa-ink font-bold uppercase tracking-widest rounded-sm px-8 h-12">
                  Register for Tournament <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/tournaments" data-testid="hero-tournaments-btn">
                <Button size="lg" variant="outline" className="border-white/40 text-white hover:bg-white hover:text-jpa-ink font-bold uppercase tracking-widest rounded-sm px-8 h-12 bg-transparent">
                  View Tournaments
                </Button>
              </Link>
            </div>

            {/* Quick stats */}
            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl">
              {[
                { icon: Users, k: "300+", l: "Registered players" },
                { icon: Trophy, k: "14", l: "Tournaments hosted" },
                { icon: MapPin, k: "13", l: "Affiliated districts" },
                { icon: Calendar, k: "2019", l: "Founded" },
              ].map(({ icon: Icon, k, l }, i) => (
                <div key={i}>
                  <Icon className="w-5 h-5 text-jpa-accent mb-2" />
                  <div className="font-display text-4xl tracking-wider">{k}</div>
                  <div className="text-xs uppercase tracking-widest text-white/70">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Marquee ticker */}
        <div className="absolute bottom-0 inset-x-0 bg-jpa-accent text-jpa-ink py-3 border-t-4 border-jpa-primary">
          <Marquee speed={45} gradient={false} pauseOnHover>
            {[...running, ...upcoming, ...tournaments.filter((t) => t.status === "past").slice(0, 3)].map((t) => (
              <span key={t.id} className="inline-flex items-center gap-3 font-bold uppercase tracking-widest text-sm px-8">
                <span className={`w-2 h-2 rounded-full ${t.status === "running" ? "bg-jpa-live live-pulse" : "bg-jpa-ink"}`} />
                {t.status.toUpperCase()} • {t.name} • {t.location} • {t.start_date}
              </span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* UPCOMING TOURNAMENTS */}
      <section data-testid="upcoming-tournaments-section" className="max-w-[1400px] mx-auto px-4 md:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-jpa-primary">The calendar</div>
            <h2 className="font-display text-5xl md:text-6xl tracking-wider">Upcoming Tournaments</h2>
          </div>
          <Link to="/tournaments" className="hidden md:inline text-sm font-bold uppercase tracking-widest text-jpa-primary hover:text-jpa-primary-hover">
            View all →
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {upcoming.map((t) => <TournamentCard key={t.id} t={t} />)}
        </div>
      </section>

      {/* FEATURED PLAYERS / RANKINGS PREVIEW */}
      <section data-testid="rankings-preview-section" className="bg-jpa-ink text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-xs font-bold uppercase tracking-widest text-jpa-accent">Top players</div>
            <h2 className="font-display text-5xl md:text-6xl tracking-wider mt-2">State Rankings</h2>
            <p className="mt-4 text-white/70 max-w-md">
              Track the best pickleball athletes from Jharkhand. Points are earned in JPA-sanctioned events.
            </p>
            <Link to="/rankings" className="inline-block mt-6">
              <Button className="bg-jpa-accent text-jpa-ink hover:bg-[#BEE617] font-bold uppercase tracking-widest rounded-sm px-6">
                Full rankings →
              </Button>
            </Link>
          </div>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] font-bold uppercase tracking-widest text-white/60 mr-2">Category:</span>
              <button
                data-testid="rank-toggle-m"
                onClick={() => setRankGender("M")}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest rounded-sm border transition-colors ${rankGender === "M" ? "bg-jpa-accent text-jpa-ink border-jpa-accent" : "bg-transparent text-white border-white/25 hover:border-jpa-accent"}`}
              >
                Men&apos;s Singles 19+
              </button>
              <button
                data-testid="rank-toggle-f"
                onClick={() => setRankGender("F")}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest rounded-sm border transition-colors ${rankGender === "F" ? "bg-jpa-accent text-jpa-ink border-jpa-accent" : "bg-transparent text-white border-white/25 hover:border-jpa-accent"}`}
              >
                Women&apos;s Singles 19+
              </button>
            </div>
            <div className="border border-white/15">
              <div className="grid grid-cols-12 bg-white/5 py-3 px-4 text-[10px] font-bold uppercase tracking-widest text-white/60">
                <div className="col-span-1">Rank</div>
                <div className="col-span-6">Player</div>
                <div className="col-span-3">Category</div>
                <div className="col-span-2 text-right">Points</div>
              </div>
              {rankings.length === 0 && (
                <div className="py-8 px-4 text-center text-white/50 text-sm border-t border-white/10">
                  No rankings published yet for this category.
                </div>
              )}
              {rankings.slice(0, 10).map((r) => (
                <Link key={r.id || `${r.player_id}-${r.event_category}-${r.age_category}`} to={r.player_id ? `/players/${r.player_id}` : "/rankings"} className="grid grid-cols-12 items-center py-2 md:py-3 px-3 md:px-4 border-t border-white/10 hover:bg-white/5 transition-colors">
                  <div className="col-span-1 font-display text-2xl text-jpa-accent">{r.rank}</div>
                  <div className="col-span-6 flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-sm overflow-hidden bg-white/10 flex-shrink-0">
                      {r.player_image && <img src={r.player_image} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold uppercase text-sm truncate">{r.player_name || "—"}</div>
                      <div className="text-[10px] uppercase tracking-widest text-white/50 truncate">{r.district || ""}</div>
                    </div>
                  </div>
                  <div className="col-span-3 text-xs text-white/80">
                    {r.event_label || "Singles"}{r.age_label ? ` · ${r.age_label}` : ""}
                  </div>
                  <div className="col-span-2 text-right font-bold">{r.points}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* JPA STARS */}
      <section data-testid="jpa-stars-section" className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-20">
        <div className="flex items-end justify-between mb-8 md:mb-10">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-jpa-primary">Jharkhand&apos;s finest</div>
            <h2 className="font-display text-5xl md:text-6xl tracking-wider leading-none">JPA Stars</h2>
            <p className="text-jpa-muted text-sm md:text-base mt-2 max-w-xl">The big names driving pickleball in Jharkhand — multi-time champions and national qualifiers.</p>
          </div>
          <Link to="/players" className="hidden md:inline text-sm font-bold uppercase tracking-widest text-jpa-primary hover:text-jpa-primary-hover">
            All players →
          </Link>
        </div>
        {stars.length === 0 ? (
          <div className="border border-dashed border-jpa-border rounded-sm p-16 text-center text-jpa-muted">
            JPA Stars will appear here soon. (Admins can add up to 10 in the admin panel.)
          </div>
        ) : (
          <Marquee speed={40} gradient={false} pauseOnHover autoFill>
            {stars.slice(0, 10).map((s, i) => (
              <div key={s.id || i} data-testid={`star-slide-${s.id || i}`} className="mx-2 md:mx-3 w-[220px] md:w-[280px] group relative overflow-hidden border border-jpa-border">
                <div className="aspect-[3/4] bg-jpa-surface overflow-hidden">
                  <img src={s.image_url} alt={s.name || `JPA star ${i + 1}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-jpa-ink via-jpa-ink/30 to-transparent" />
                <div className="absolute top-2 left-2 bg-jpa-accent text-jpa-ink px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest skew-badge">
                  Star #{i + 1}
                </div>
                {(s.name || s.caption) && (
                  <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 text-white">
                    {s.name && <div className="font-display text-lg md:text-xl tracking-wider leading-none">{s.name}</div>}
                    {s.caption && <div className="text-[10px] uppercase tracking-widest text-white/80 mt-1 line-clamp-1">{s.caption}</div>}
                  </div>
                )}
              </div>
            ))}
          </Marquee>
        )}
      </section>

      {/* NEWS */}
      <section data-testid="news-preview-section" className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-20">
        <div className="flex items-end justify-between mb-8 md:mb-10">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-jpa-primary">Latest news</div>
            <h2 className="font-display text-5xl md:text-6xl tracking-wider">Press Centre</h2>
          </div>
          <Link to="/news" className="hidden md:inline text-sm font-bold uppercase tracking-widest text-jpa-primary hover:text-jpa-primary-hover">
            All news →
          </Link>
        </div>
        <div className="grid lg:grid-cols-12 gap-4 md:gap-6">
          {featuredNews && (
            <Link to={`/news/${featuredNews.id}`} className="lg:col-span-8 relative group aspect-[4/3] md:aspect-[16/9] overflow-hidden bg-jpa-ink block">
              <img src={featuredNews.image_url} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-4 md:p-8 text-white">
                <span className="bg-jpa-accent text-jpa-ink px-2 py-1 text-[10px] font-bold uppercase tracking-widest w-fit skew-badge">{featuredNews.category}</span>
                <h3 className="font-display text-2xl md:text-4xl lg:text-5xl tracking-wider mt-3 md:mt-4 max-w-2xl leading-tight break-words">{featuredNews.title}</h3>
                <p className="mt-2 md:mt-3 text-white/85 max-w-2xl text-sm md:text-base line-clamp-2 md:line-clamp-none">{featuredNews.excerpt}</p>
              </div>
            </Link>
          )}
          <div className="lg:col-span-4 flex flex-col gap-3 md:gap-4">
            {otherNews.map((n) => (
              <Link key={n.id} to={`/news/${n.id}`} className="group grid grid-cols-3 gap-3 items-center border border-jpa-border p-3 hover:border-jpa-primary transition-colors">
                <div className="aspect-[4/3] overflow-hidden bg-jpa-surface">
                  {n.image_url && <img src={n.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />}
                </div>
                <div className="col-span-2 min-w-0">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-jpa-primary">{n.category}</span>
                  <div className="font-semibold text-sm mt-1 line-clamp-2 group-hover:text-jpa-primary transition-colors break-words">{n.title}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SPONSORS */}
      <section data-testid="sponsors-strip" className="bg-jpa-surface py-16 border-y border-jpa-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center mb-8">
            <div className="text-xs font-bold uppercase tracking-widest text-jpa-primary">Powered by</div>
            <h2 className="font-display text-4xl tracking-wider">Our Prime Sponsors & Partners</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 items-stretch">
            {sponsors.slice(0, 6).map((s) => (
              <a key={s.id} href={s.website || "#"} target="_blank" rel="noreferrer" data-testid={`home-sponsor-${s.id}`} className="bg-white border border-jpa-border p-4 flex flex-col items-center justify-center gap-2 min-h-[120px] hover:border-jpa-primary transition-colors">
                <img
                  src={s.logo_url}
                  alt={s.name}
                  onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "block"; }}
                  className="max-h-12 w-auto object-contain"
                />
                <span style={{display: "none"}} className="font-display text-xl tracking-wider text-jpa-primary text-center">{s.name}</span>
                <span className="text-[11px] font-bold uppercase tracking-widest text-jpa-ink text-center line-clamp-1">{s.name}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* AIPA AFFILIATION */}
      <section data-testid="aipa-affiliation" className="relative bg-white py-24 overflow-hidden">
        <div className="absolute inset-0 grain opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-4 flex justify-center lg:justify-end">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-jpa-accent/60 to-jpa-primary/30 blur-2xl rounded-full" />
              <div className="relative bg-white p-6 md:p-8 rounded-sm shadow-xl border-2 border-jpa-primary">
                <img
                  src="/logos/aipa-logo.png"
                  alt="AIPA — All India Pickleball Association"
                  data-testid="aipa-logo"
                  className="w-48 md:w-56 lg:w-64 h-auto object-contain"
                />
              </div>
            </div>
          </div>
          <div className="lg:col-span-8 text-center lg:text-left">
            <span className="inline-block bg-jpa-accent text-jpa-ink px-3 py-1 text-xs font-bold uppercase tracking-widest skew-badge">Officially certified</span>
            <h2 className="font-display text-6xl md:text-8xl lg:text-9xl tracking-wider mt-4 leading-[0.9] text-jpa-ink">
              AFFILIATED <span className="text-jpa-primary">BY AIPA</span>
            </h2>
            <p className="mt-5 max-w-2xl text-lg text-jpa-muted mx-auto lg:mx-0">
              The Jharkhand Pickleball Association is an <b className="text-jpa-ink">officially recognised state chapter</b> of the
              All India Pickleball Association. Every JPA-sanctioned event feeds into national rankings, and our players
              qualify directly for AIPA national circuits.
            </p>
            <div className="mt-6 flex flex-wrap justify-center lg:justify-start gap-6 text-sm">
              <div><span className="font-display text-3xl text-jpa-primary block leading-none">2024</span><span className="uppercase tracking-widest text-xs text-jpa-muted">Renewed</span></div>
              <div className="border-l border-jpa-border pl-6"><span className="font-display text-3xl text-jpa-primary block leading-none">JH</span><span className="uppercase tracking-widest text-xs text-jpa-muted">Affiliation code</span></div>
              <div className="border-l border-jpa-border pl-6"><span className="font-display text-3xl text-jpa-primary block leading-none">National</span><span className="uppercase tracking-widest text-xs text-jpa-muted">Ranking feed</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 bg-jpa-primary text-white overflow-hidden">
        <div className="absolute inset-0 grain opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-jpa-accent">Join the movement</div>
            <h2 className="font-display text-5xl md:text-7xl tracking-wider mt-2">READY TO<br />COMPETE?</h2>
            <p className="mt-4 max-w-md text-white/80">Register for the next tournament and get ranked on the state ladder. Rankings, matches and coaching resources — all in one place.</p>
            <Link to="/tournaments">
              <Button size="lg" className="mt-6 bg-jpa-accent text-jpa-ink hover:bg-[#BEE617] font-bold uppercase tracking-widest rounded-sm px-8 h-12">
                Register for Tournament
              </Button>
            </Link>
          </div>
          <div className="relative aspect-[3/4] max-h-[520px] mx-auto md:mx-0 overflow-hidden rounded-sm shadow-2xl">
            <img
              src="/media/join-jpa-poster.png"
              alt="Join JPA now and lead the world"
              data-testid="ready-to-compete-poster"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
